import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  Mail,
  CheckCircle2,
  Loader2,
  X,
  Shield,
  Building2,
  Plus
} from 'lucide-react';
import { Button, Select, Input, PageHeader, InputValidador, TextButton, MensajeInformativo, Tooltip, useRevealOnLoad, RevealSkeleton, RevealField, AvisoAutocompletado } from '@/contasis';
import { ToastNotifications } from '@/components/Toast';
import { useToast } from '@/contexts/ContextoToast';
import { TarjetaConfiguracion } from '@/components/Empresa/TarjetaConfiguracion';
import { IndicadorEstado } from '@/components/Empresa/IndicadorEstado';
import { ModalConfirmacion } from '@/components/Empresa/ModalConfirmacion';
import { sunatService } from '@/services/api';
import type { SunatRucData } from '@/services/api/sunat.service';
import { monedasToSelectOptions } from '@/shared/moneda/monedas';
import { useMonedas } from '@/shared/moneda/useMonedas';
import { useEmpresas } from '../../../../../../hooks/useEmpresas';
import type { CreateEmpresaDTO, UpdateEmpresaDTO } from '../../../../../../services/api/empresas.service';
import { useWorkspace } from '@/contexts/ContextoWorkspace';
import { useUbigeoCascade } from '@/services/api/ubigeo/hooks/useUbigeoCascade';
import { ContenedorForm } from '@/components/Configuracion/ContenedorConfiguracion';
import { construirUrlSuscripcion } from '@/utils/enlacesBilling';
import { ModalRedireccionPago } from '@/components/ModalRedireccionPago';

interface EmpresaFormData {
  ruc: string;
  razonSocial: string;
  nombreComercial: string;
  direccionFiscal: string;
  codigoDepartamento: string;
  departamento: string;
  codigoProvincia: string;
  provincia: string;
  codigoDistrito: string;
  distrito: string;
  codigoPostal: string;
  telefonos: string[];
  correosElectronicos: string[];
  sitioWeb: string;
  actividadEconomica: string;
  regimenTributario: string;
  monedaBase: string;
  ambienteSunat: string;
  facturarEn: string;
  esActivo: boolean;
  esPrincipal: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const REGIMENES_TRIBUTARIOS = [
  { value: '', label: 'Selecciona un régimen tributario', disabled: true },
  { value: 'Nuevo Régimen Único Simplificado (Nuevo RUS)', label: 'Nuevo Régimen Único Simplificado (Nuevo RUS)' },
  { value: 'Régimen Especial de Renta (RER)',              label: 'Régimen Especial de Renta (RER)' },
  { value: 'Régimen MYPE Tributario (RMT)',                label: 'Régimen MYPE Tributario (RMT)' },
  { value: 'Régimen General de Renta (RG)',                label: 'Régimen General de Renta (RG)' },
];

const REGIMEN_LEGACY: Record<string, string> = {
  NUEVO_RUS: 'Nuevo Régimen Único Simplificado (Nuevo RUS)',
  RER:       'Régimen Especial de Renta (RER)',
  RMT:       'Régimen MYPE Tributario (RMT)',
  GENERAL:   'Régimen General de Renta (RG)',
};

const normalizarRegimen = (valor: string): string =>
  REGIMEN_LEGACY[valor] ?? valor;

const FORM_INICIAL: EmpresaFormData = {
  ruc: '',
  razonSocial: '',
  nombreComercial: '',
  direccionFiscal: '',
  codigoDepartamento: '',
  departamento: '',
  codigoProvincia: '',
  provincia: '',
  codigoDistrito: '',
  distrito: '',
  codigoPostal: '',
  telefonos: [''],
  correosElectronicos: [''],
  sitioWeb: '',
  actividadEconomica: '',
  regimenTributario: '',
  monedaBase: 'PEN',
  ambienteSunat: 'PRUEBA',
  facturarEn: 'SUNAT',
  esActivo: true,
  esPrincipal: false,
};

export function ConfiguracionEmpresaIndex() {
  const { empresaId } = useParams<{ empresaId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const origenNavegacion = (location.state as { from?: string })?.from || 'configuracion';
  const [searchParams] = useSearchParams();
  const planSku = searchParams.get('planSku') ?? undefined;
  const tipoSuscripcion = searchParams.get('tipoSuscripcion') ?? undefined;
  const { empresas: empresasWorkspace, setWorkspace } = useWorkspace();

  const {
    empresa,
    esCargando,
    esGuardando,
    cargarEmpresa,
    crearEmpresa,
    actualizarEmpresa,
    setPrincipal,
  } = useEmpresas();

  const monedas = useMonedas();
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const [datosFormulario, setDatosFormulario] = useState<EmpresaFormData>(FORM_INICIAL);
  const [erroresFormulario, setErroresFormulario] = useState<Record<string, string>>({});
  const [validacionRuc, setValidacionRuc] = useState<{
    isValid: boolean;
    message: string;
    data?: {
      razonSocial: string;
      nombreComercial: string;
      direccionFiscal: string;
      ubigeo: string;
      actividadEconomica: string;
      codigoDepartamento: string;
      codigoProvincia: string;
      codigoDistrito: string;
      departamento: string;
      provincia: string;
      distrito: string;
    };
  } | null>(null);
  const [sunatContribuyente, setSunatContribuyente] = useState<Pick<SunatRucData, 'estado' | 'condicion' | 'fechaBaja' | 'esPrico'> | null>(null);
  const reveal = useRevealOnLoad();
  const [tieneCambios, setTieneCambios] = useState(false);
  const [datosOriginales, setDatosOriginales] = useState<EmpresaFormData | null>(null);
  const [, setMostrarModalProduccion] = useState(false);
  const [mostrarModalRedireccionPago, setMostrarModalRedireccionPago] = useState(false);
  const [urlRedireccionPago, setUrlRedireccionPago] = useState<string | null>(null);
  const ubigeoInicializado = useRef(false);
  const lastCargarEmpresaIdRef = useRef<string | undefined>(undefined);

  const {
    departamentos,
    provincias,
    distritos,
    setDepartamento,
    setProvincia,
    setDistrito,
  } = useUbigeoCascade();

  useEffect(() => {
    ubigeoInicializado.current = false;
    if (!empresaId || empresaId === 'crear' || empresaId === 'create') return;
    if (lastCargarEmpresaIdRef.current === empresaId) return;
    lastCargarEmpresaIdRef.current = empresaId;
    cargarEmpresa(empresaId);
  }, [empresaId, cargarEmpresa]);

  // Cargar datos de la empresa en el formulario
  useEffect(() => {
    if (!empresa || !empresaId || empresaId === 'crear' || empresaId === 'create') {
      return;
    }

    const loadedData: EmpresaFormData = {
      ruc: empresa.ruc || '',
      razonSocial: empresa.razonSocial || '',
      nombreComercial: empresa.nombreComercial || '',
      direccionFiscal: empresa.direccionFiscal || '',
      codigoDepartamento: empresa.codigoDepartamento || '',
      departamento: empresa.departamento || '',
      codigoProvincia: empresa.codigoProvincia || '',
      provincia: empresa.provincia || '',
      codigoDistrito: empresa.codigoDistrito || '',
      distrito: empresa.distrito || '',
      codigoPostal: empresa.codigoPostal || '',
      telefonos: (empresa.telefonos && empresa.telefonos.length > 0) ? empresa.telefonos : [''],
      correosElectronicos: (empresa.correosElectronicos && empresa.correosElectronicos.length > 0) ? empresa.correosElectronicos : [''],
      sitioWeb: empresa.sitioWeb || '',
      actividadEconomica: empresa.actividadEconomica || '',
      regimenTributario: normalizarRegimen(empresa.regimenTributario || 'GENERAL'),
      monedaBase: empresa.monedaBase || 'PEN',
      ambienteSunat: empresa.ambienteSunat || 'PRUEBA',
      facturarEn: empresa.facturarEn || 'SUNAT',
      esActivo: empresa.esActivo ?? false,
      esPrincipal: empresa.esPrincipal ?? false,
    };
    
    setDatosFormulario(loadedData);
    setDatosOriginales(loadedData);
    setTieneCambios(false);

    if (empresa.ruc) {
      setValidacionRuc({
        isValid: true,
        message: 'RUC validado desde el sistema',
        data: {
          razonSocial: empresa.razonSocial || '',
          nombreComercial: empresa.nombreComercial || '',
          direccionFiscal: empresa.direccionFiscal || '',
          ubigeo: empresa.codigoPostal || '',
          actividadEconomica: empresa.actividadEconomica || '',
          codigoDepartamento: empresa.codigoDepartamento || '',
          codigoProvincia: empresa.codigoProvincia || '',
          codigoDistrito: empresa.codigoDistrito || '',
          departamento: empresa.departamento || '',
          provincia: empresa.provincia || '',
          distrito: empresa.distrito || '',
        }
      });
    }
  }, [empresa, empresaId]);

  // Inicializar ubigeo cascade una sola vez cuando empresa y departamentos estén disponibles
  useEffect(() => {
    if (ubigeoInicializado.current || !empresa || !departamentos.length) return;
    if (!empresa.codigoDepartamento) return;

    const dept = departamentos.find(d => d.codigo === empresa.codigoDepartamento);
    if (dept) {
      ubigeoInicializado.current = true;
      setDepartamento(dept);
    }
  }, [empresa, departamentos, setDepartamento]);

  useEffect(() => {
    if (!ubigeoInicializado.current || !empresa || !provincias.length) return;
    if (!empresa.codigoProvincia) return;

    const prov = provincias.find(p => p.codigo === empresa.codigoProvincia);
    if (prov) setProvincia(prov);
  }, [empresa, provincias, setProvincia]);

  useEffect(() => {
    if (!ubigeoInicializado.current || !empresa || !distritos.length) return;
    if (!empresa.codigoDistrito) return;

    const dist = distritos.find(d => d.codigo === empresa.codigoDistrito);
    if (dist) setDistrito(dist);
  }, [empresa, distritos, setDistrito]);

  useEffect(() => {
    if (!datosOriginales) {
      setTieneCambios(true);
      return;
    }

    const hasFormChanges =
      datosFormulario.ruc !== datosOriginales.ruc ||
      datosFormulario.razonSocial !== datosOriginales.razonSocial ||
      datosFormulario.nombreComercial !== datosOriginales.nombreComercial ||
      datosFormulario.direccionFiscal !== datosOriginales.direccionFiscal ||
      datosFormulario.codigoDepartamento !== datosOriginales.codigoDepartamento ||
      datosFormulario.codigoProvincia !== datosOriginales.codigoProvincia ||
      datosFormulario.codigoDistrito !== datosOriginales.codigoDistrito ||
      datosFormulario.actividadEconomica !== datosOriginales.actividadEconomica ||
      datosFormulario.regimenTributario !== datosOriginales.regimenTributario ||
      datosFormulario.monedaBase !== datosOriginales.monedaBase ||
      datosFormulario.ambienteSunat !== datosOriginales.ambienteSunat ||
      datosFormulario.facturarEn !== datosOriginales.facturarEn ||
      JSON.stringify(datosFormulario.telefonos) !== JSON.stringify(datosOriginales.telefonos) ||
      JSON.stringify(datosFormulario.correosElectronicos) !== JSON.stringify(datosOriginales.correosElectronicos);

    setTieneCambios(hasFormChanges);
  }, [datosFormulario, datosOriginales]);

  const manejarValidacionRuc = (result: {
    isValid: boolean;
    message: string;
    data?: Record<string, unknown>;
    rawData?: SunatRucData;
  }) => {
    if (result.isValid) reveal.finish();
    else reveal.fail();

    setValidacionRuc({
      isValid: result.isValid,
      message: result.message,
      data: result.data ? {
        razonSocial: (result.data.razonSocial as string) || '',
        nombreComercial: (result.data.nombreComercial as string) || '',
        direccionFiscal: (result.data.direccionFiscal as string) || '',
        ubigeo: (result.data.ubigeo as string) || '',
        actividadEconomica: (result.data.actividadEconomica as string) || '',
        codigoDepartamento: (result.data.codigoDepartamento as string) || '',
        codigoProvincia: (result.data.codigoProvincia as string) || '',
        codigoDistrito: (result.data.codigoDistrito as string) || '',
        departamento: (result.data.departamento as string) || '',
        provincia: (result.data.provincia as string) || '',
        distrito: (result.data.distrito as string) || '',
      } : undefined
    });

    setSunatContribuyente(result.isValid && result.rawData ? {
      estado: result.rawData.estado,
      condicion: result.rawData.condicion,
      fechaBaja: result.rawData.fechaBaja,
      esPrico: result.rawData.esPrico,
    } : null);

    if (result.isValid) {
      setErroresFormulario(prev => {
        const cleaned = { ...prev };
        delete cleaned.razonSocial;
        delete cleaned.direccionFiscal;
        delete cleaned.actividadEconomica;
        return cleaned;
      });

      setDatosFormulario(prev => {
        const ambienteSunat = empresa?.ambienteSunat === 'PRODUCCION'
          ? 'PRODUCCION'
          : prev.ruc === '20000000000' ? 'PRUEBA' : 'PRODUCCION';
        const facturarEn = result.rawData?.esPrico ? 'BIZLINKS' : 'SUNAT';

        if (!result.data) {
          return { ...prev, ambienteSunat, facturarEn };
        }

        const data = result.data;
        return {
          ...prev,
          ambienteSunat,
          facturarEn,
          razonSocial: (data.razonSocial as string) || prev.razonSocial,
          nombreComercial: (data.nombreComercial as string)?.trim() || prev.nombreComercial || '-',
          direccionFiscal: (data.direccionFiscal as string) || prev.direccionFiscal,
          codigoPostal: (data.ubigeo as string) || prev.codigoPostal,
          actividadEconomica: (data.actividadEconomica as string) || prev.actividadEconomica,
          codigoDepartamento: (data.codigoDepartamento as string) || prev.codigoDepartamento,
          departamento: (data.departamento as string) || prev.departamento,
          codigoProvincia: (data.codigoProvincia as string) || prev.codigoProvincia,
          provincia: (data.provincia as string) || prev.provincia,
          codigoDistrito: (data.codigoDistrito as string) || prev.codigoDistrito,
          distrito: (data.distrito as string) || prev.distrito,
        };
      });
    }
  };

  const isEditMode = empresaId && empresaId !== 'crear' && empresaId !== 'create';
  const esEmpresaDemo = empresa?.ruc === '20000000000';
  const rucBloqueadoPorComprobantes = Boolean(isEditMode) && empresa?.tieneComprobantes === true;
  const sunatEstadoIrregular = Boolean(sunatContribuyente) && (
    Boolean(sunatContribuyente?.estado?.trim()) && sunatContribuyente!.estado!.trim().toUpperCase() !== 'ACTIVO'
    || Boolean(sunatContribuyente?.condicion?.trim()) && sunatContribuyente!.condicion!.trim().toUpperCase() !== 'HABIDO'
  );

  // En empresa nueva, el resto del formulario aparece solo con el RUC validado y sin estado irregular en SUNAT
  const mostrarCamposEmpresa = Boolean(isEditMode) || (validacionRuc?.isValid === true && !sunatEstadoIrregular);
  // Al editar una empresa existente los campos se ven de inmediato; la animación es solo para la creación
  const revealInmediato = Boolean(isEditMode);
  const revealVisible = revealInmediato || reveal.isSuccess;

  const validarFormulario = (): boolean => {
    const errors: Record<string, string> = {};

    if (sunatEstadoIrregular) {
      errors.ruc = `No se puede registrar: SUNAT reporta estado "${sunatContribuyente?.estado}" y condición "${sunatContribuyente?.condicion}" para este RUC.`;
    }
    
    if (!datosFormulario.ruc || datosFormulario.ruc.length !== 11) {
      errors.ruc = 'RUC debe tener 11 dígitos';
    } else if (!validacionRuc?.isValid && !isEditMode) {
      errors.ruc = 'Debes validar el RUC con SUNAT';
    }

    if (!datosFormulario.razonSocial) {
      errors.razonSocial = 'Razón Social es requerida';
    }
    if (!datosFormulario.direccionFiscal) {
      errors.direccionFiscal = 'Dirección Fiscal es requerida';
    }
    if (!datosFormulario.regimenTributario) {
      errors.regimenTributario = 'Debes seleccionar un régimen tributario';
    }

    // Validar correos electrónicos
    datosFormulario.correosElectronicos.forEach((email, index) => {
      if (email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          errors[`email_${index}`] = 'Formato de correo inválido';
        }
      }
    });

    // Validar teléfonos
    datosFormulario.telefonos.forEach((phone, index) => {
      if (phone.trim()) {
        const phoneRegex = /^[+]?[0-9\s()-]{7,}$/;
        if (!phoneRegex.test(phone)) {
          errors[`phone_${index}`] = 'Formato de teléfono inválido';
        }
      }
    });

    setErroresFormulario(errors);
    return Object.keys(errors).length === 0;
  };

  const manejarCambioCampoArray = (
    field: 'telefonos' | 'correosElectronicos',
    index: number,
    value: string
  ) => {
    setDatosFormulario(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const agregarCampoArray = (field: 'telefonos' | 'correosElectronicos') => {
    setDatosFormulario(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removerCampoArray = (field: 'telefonos' | 'correosElectronicos', index: number) => {
    setDatosFormulario(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

const manejarCambioInput = (name: keyof EmpresaFormData, value: string) => {
    setDatosFormulario((prev) => ({ ...prev, [name]: value }));
    if (erroresFormulario[name]) {
      setErroresFormulario((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const onSubmitGuardar = async (e: React.FormEvent) => {
    e.preventDefault();

    if (esEmpresaDemo) {
      showError('No se puede editar una empresa Demo');
      return;
    }

    if (!validarFormulario()) {
      showError('Completa los campos requeridos antes de continuar');
      return;
    }

    const payload: CreateEmpresaDTO | UpdateEmpresaDTO = {
      ruc: datosFormulario.ruc.trim(),
      razonSocial: datosFormulario.razonSocial.trim(),
      nombreComercial: datosFormulario.nombreComercial?.trim() || '-',
      direccionFiscal: datosFormulario.direccionFiscal?.trim() || undefined,
      codigoDepartamento: datosFormulario.codigoDepartamento || undefined,
      departamento: datosFormulario.departamento || undefined,
      codigoProvincia: datosFormulario.codigoProvincia || undefined,
      provincia: datosFormulario.provincia || undefined,
      codigoDistrito: datosFormulario.codigoDistrito || undefined,
      distrito: datosFormulario.distrito || undefined,
      codigoPostal: datosFormulario.codigoPostal || undefined,
      telefonos: datosFormulario.telefonos.filter(t => t.trim()).length > 0 ? datosFormulario.telefonos.filter(t => t.trim()) : undefined,
      correosElectronicos: datosFormulario.correosElectronicos.filter(e => e.trim()).length > 0 ? datosFormulario.correosElectronicos.filter(e => e.trim()) : undefined,
      sitioWeb: datosFormulario.sitioWeb || undefined,
      actividadEconomica: datosFormulario.actividadEconomica || undefined,
      regimenTributario: datosFormulario.regimenTributario || undefined,
      monedaBase: datosFormulario.monedaBase || undefined,
      ambienteSunat: datosFormulario.ambienteSunat || undefined,
      facturarEn: datosFormulario.facturarEn || 'SUNAT',
      esActivo: datosFormulario.esActivo,
      esPrincipal: datosFormulario.esPrincipal,
      planSku: !isEditMode ? planSku : undefined,
    };

    try {
      if (isEditMode) {
        const result = await actualizarEmpresa({ ...payload, id: empresaId } as UpdateEmpresaDTO);
        if (result) {
          showSuccess('Empresa actualizada exitosamente');
          if (origenNavegacion === 'administrar-empresas') {
            setTimeout(() => navigate('/administrar-empresas'), 1500);
          } else {
            cargarEmpresa(empresaId);
            setTieneCambios(false);
          }
        }
      } else {
        const result = await crearEmpresa(payload as CreateEmpresaDTO);
        if (result) {
          const esLaPrimeraEmpresaReal = !empresasWorkspace.some(e => e.ruc !== '20000000000');
          if (esLaPrimeraEmpresaReal) {
            await setPrincipal(result.id);
          }
          setWorkspace(result.id, result.establecimientoPrincipal?.id);
          showSuccess(`Empresa "${result.razonSocial}" creada exitosamente`);

          const sedePrincipalId = result.establecimientoPrincipal?.id;
          const sinUbigeo = !!sedePrincipalId && (
            !datosFormulario.codigoDepartamento ||
            !datosFormulario.codigoProvincia ||
            !datosFormulario.codigoDistrito
          );

          if (tipoSuscripcion === 'compra') {
            setUrlRedireccionPago(construirUrlSuscripcion(result.id, { view: 'pago', sku: planSku }));
            setMostrarModalRedireccionPago(true);
          } else if (sinUbigeo) {
            setTimeout(() =>
              navigate('/configuracion/establecimientos', {
                state: { avisoUbigeoEstablecimientoId: sedePrincipalId }
              }),
              1500
            );
          } else {
            setTimeout(() =>
              navigate('/comprobantes', { state: { mostrarValidacionSunat: true } }),
              1500
            );
          }
        }
      }
    } catch (error) {
      const mensajeError = error instanceof Error ? error.message : (isEditMode ? 'Error al actualizar empresa' : 'Error al crear empresa');
      showError(mensajeError);
    }
  };

  const isFormValid = datosFormulario.ruc.length === 11 &&
    datosFormulario.razonSocial.trim() !== '' &&
    datosFormulario.direccionFiscal.trim() !== '' &&
    datosFormulario.regimenTributario.trim() !== '' &&
    (isEditMode ? true : validacionRuc?.isValid === true) &&
    tieneCambios;

  const handleClickVolver = () => {
    navigate(-1);
  };

  const handleClickCancelar = () => {
    navigate(-1);
  };

  const handleClickCerrarModal = () => {
    setMostrarModalProduccion(false);
  };

  const handleClickRemoveTelefono = (index: number) => {
    removerCampoArray('telefonos', index);
  };

  const handleClickAddTelefono = () => {
    agregarCampoArray('telefonos');
  };

  const handleClickRemoveEmail = (index: number) => {
    removerCampoArray('correosElectronicos', index);
  };

  const handleClickAddEmail = () => {
    agregarCampoArray('correosElectronicos');
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={isEditMode ? "Configuración de Empresa" : "Crear una nueva empresa"}
        actions={
          <TextButton
            id="empresa-volver"
            name="empresaVolver"
            size="md"
            iconLeft={<ArrowLeft size={16} />}
            onClick={handleClickVolver}
          >
            Volver
          </TextButton>
        }
      />

      <ToastNotifications toasts={toasts} onRemove={removeToast} />

      <ModalRedireccionPago
        isOpen={mostrarModalRedireccionPago}
        onRedirect={() => {
          if (urlRedireccionPago) window.location.href = urlRedireccionPago;
        }}
      />

      <div className="flex-1 overflow-auto">
        <ContenedorForm>
          <form onSubmit={onSubmitGuardar} className="space-y-6">
            <TarjetaConfiguracion
              title="Datos de tu empresa"
              density="compact"
            >
              <div className="space-y-4">
                {esEmpresaDemo && (
                  <MensajeInformativo variant="warning">
                    No se puede editar una empresa Demo.
                  </MensajeInformativo>
                )}
                {rucBloqueadoPorComprobantes && (
                  <MensajeInformativo variant="info">
                    El RUC no se puede modificar porque esta empresa ya tiene comprobantes registrados.
                  </MensajeInformativo>
                )}
                {/* RUC InputValidador Component */}
                <InputValidador
                  id="empresa-ruc"
                  name="empresaRuc"
                  label="RUC"
                  required
                  value={datosFormulario.ruc}
                  onChange={(ruc: string) => {
                    manejarCambioInput('ruc', ruc);
                  }}
                  validationService={sunatService.validateRuc}
                  onValidated={manejarValidacionRuc}
                  onValidationStart={reveal.start}
                  onToast={(type, message) => {
                    if (type === 'error') showError(message);
                    else if (type === 'success') showSuccess(message);
                  }}
                  leftIcon={<Building2 size={18} />}
                  validateButtonText="Consultar SUNAT"
                  validatedButtonText="Validado"
                  loadingText="Consultando SUNAT"
                  successMessage="RUC validado correctamente."
                  successDescription="Datos completados automáticamente desde SUNAT."
                  retryText="Intentar nuevamente"
                  hideRazonSocialEcho
                  ocultarMensajeExito={sunatEstadoIrregular}
                  notaDebajo={
                    sunatContribuyente?.esPrico && !sunatEstadoIrregular ? (
                      <MensajeInformativo
                        variant="info"
                        discreto
                        unido="arriba"
                        action={
                          <Tooltip
                            text="Tu prueba gratis continúa con normalidad. Esta configuración se aplica automáticamente según la condición de tu RUC. Si luego eliges un plan, podrás revisar todos los detalles antes de confirmar."
                            position="top"
                            align="end"
                          >
                            <button
                              id="empresa-emision-ayuda"
                              name="empresaEmisionAyuda"
                              type="button"
                              className="text-xs font-medium text-brand underline underline-offset-2 whitespace-nowrap rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                            >
                              ¿Qué significa?
                            </button>
                          </Tooltip>
                        }
                      >
                        <span className="inline-flex items-center justify-center h-5 px-1.5 mr-1.5 align-top rounded border border-brand bg-surface-0 text-caption-medium leading-none text-brand">
                          PRICO
                        </span>
                        <span className="font-medium text-primary">identificado</span>
                        {' · '}
                        Validación OSE requerida
                      </MensajeInformativo>
                    ) : undefined
                  }
                  minLengthToValidate={11}
                  maxLength={11}
                  placeholder="Ingresa el RUC de 11 dígitos"
                  className="font-mono"
                  disabled={esEmpresaDemo || rucBloqueadoPorComprobantes}
                />

                {!isEditMode && !reveal.isLoading && !validacionRuc?.isValid && <AvisoAutocompletado />}

                {sunatEstadoIrregular && sunatContribuyente && (
                  <MensajeInformativo variant="error">
                    <p><strong>Estado del Contribuyente:</strong> {sunatContribuyente.estado || '—'}</p>
                    {sunatContribuyente.fechaBaja && (
                      <p><strong>Fecha de Baja:</strong> {sunatContribuyente.fechaBaja}</p>
                    )}
                    <p><strong>Condición del Contribuyente:</strong> {sunatContribuyente.condicion || '—'}</p>
                    {sunatContribuyente.condicion?.trim().toUpperCase() === 'NO HABIDO' && (
                      <p className="mt-1">
                        Deberá declarar el nuevo domicilio fiscal o confirmar el señalado en el RUC. Para ello,
                        deberá acercarse a los Centros de Servicios al Contribuyente con los documentos que
                        sustenten el nuevo domicilio.
                      </p>
                    )}
                    <p className="mt-1 font-semibold">No se puede registrar esta empresa mientras el RUC tenga esta condición en SUNAT.</p>
                  </MensajeInformativo>
                )}

                {reveal.isLoading && !isEditMode && (
                  <div className="space-y-4">
                    {['w-24', 'w-32', 'w-28', 'w-36', 'w-32'].map((anchoEtiqueta, i) => (
                      <div key={i}>
                        <div className="h-5 flex items-center mb-1.5">
                          <RevealSkeleton className={`h-3 rounded ${anchoEtiqueta}`} />
                        </div>
                        <RevealSkeleton className="h-10 rounded-lg" />
                      </div>
                    ))}
                  </div>
                )}

                {mostrarCamposEmpresa && (
                <>
                <RevealField show={revealVisible} inmediato={revealInmediato} delayMs={reveal.getDelay(0)}>
                {validacionRuc?.isValid && validacionRuc.data?.razonSocial ? (
                  <Input
                    id="empresa-razon-social"
                    name="empresaRazonSocial"
                    label="Razón Social"
                    value={datosFormulario.razonSocial}
                    onChange={() => {}}
                    readOnly={true}
                    variant="validated-readonly"
                    validationMessage={{ title: "Auto-completado desde SUNAT" }}
                    showValidationMessage={true}
                    required
                    error={erroresFormulario.razonSocial}
                  />
                ) : (
                  <Input
                    id="empresa-razon-social"
                    name="empresaRazonSocial"
                    label="Razón Social"
                    type="text"
                    value={datosFormulario.razonSocial}
                    onChange={(e) => manejarCambioInput('razonSocial', e.target.value)}
                    placeholder="Se completará automáticamente al validar el RUC"
                    readOnly={!isEditMode || esEmpresaDemo}
                    disabled={!isEditMode || esEmpresaDemo}
                    rightIcon={datosFormulario.razonSocial ? <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" /> : undefined}
                    required
                    error={erroresFormulario.razonSocial}
                  />
                )}
                </RevealField>

                <RevealField show={revealVisible} inmediato={revealInmediato} delayMs={reveal.getDelay(1)}>
                <div>
                  <Input
                    id="empresa-nombre-comercial"
                    name="empresaNombreComercial"
                    label="Nombre Comercial"
                    type="text"
                    value={datosFormulario.nombreComercial}
                    onChange={(e) => manejarCambioInput('nombreComercial', e.target.value)}
                    placeholder="Nombre con el que se conoce tu empresa"
                    error={erroresFormulario.nombreComercial}
                    disabled={esEmpresaDemo}
                    required
                  />
                </div>
                </RevealField>

                <RevealField show={revealVisible} inmediato={revealInmediato} delayMs={reveal.getDelay(2)}>
                <div>
                  {validacionRuc?.isValid && validacionRuc.data?.direccionFiscal ? (
                    <Input
                      id="empresa-domicilio-fiscal"
                      name="empresaDomicilioFiscal"
                      label="Domicilio Fiscal"
                      value={datosFormulario.direccionFiscal}
                      onChange={() => {}} // No se usa porque es readonly
                      readOnly={true}
                      variant="validated-readonly"
                      validationMessage={{ title: "Auto-completado desde SUNAT" }}
                      showValidationMessage={true}
                      required
                      error={erroresFormulario.direccionFiscal}
                    />
                  ) : (
                    <Input
                      id="empresa-domicilio-fiscal"
                      name="empresaDomicilioFiscal"
                      label="Domicilio Fiscal"
                      type="text"
                      value={datosFormulario.direccionFiscal}
                      onChange={(e) => manejarCambioInput('direccionFiscal', e.target.value)}
                      placeholder="Se completará automáticamente al validar el RUC"
                      readOnly={!isEditMode || esEmpresaDemo}
                      disabled={!isEditMode || esEmpresaDemo}
                      rightIcon={datosFormulario.direccionFiscal ? <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" /> : undefined}
                      required
                      error={erroresFormulario.direccionFiscal}
                    />
                  )}
                </div>
                </RevealField>

                <RevealField show={revealVisible} inmediato={revealInmediato} delayMs={reveal.getDelay(3)}>
                {validacionRuc?.isValid && validacionRuc.data?.actividadEconomica ? (
                  <Input
                    id="empresa-actividad-economica"
                    name="empresaActividadEconomica"
                    label="Actividad Económica"
                    value={datosFormulario.actividadEconomica}
                    onChange={() => {}} // No se usa porque es readonly
                    readOnly={true}
                    variant="validated-readonly"
                    validationMessage={{ title: "Auto-completado desde SUNAT" }}
                    showValidationMessage={true}
                    required
                  />
                ) : (
                  <Input
                    id="empresa-actividad-economica"
                    name="empresaActividadEconomica"
                    label="Actividad Económica"
                    type="text"
                    value={datosFormulario.actividadEconomica}
                    onChange={(e) => manejarCambioInput('actividadEconomica', e.target.value)}
                    placeholder="Se completará automáticamente al validar el RUC"
                    readOnly={!isEditMode || esEmpresaDemo}
                    disabled={!isEditMode || esEmpresaDemo}
                    rightIcon={datosFormulario.actividadEconomica ? <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" /> : undefined}
                    required
                  />
                )}
                </RevealField>

                <RevealField show={revealVisible} inmediato={revealInmediato} delayMs={reveal.getDelay(4)}>
                <Select
                  id="empresa-regimen-tributario"
                  name="empresaRegimenTributario"
                  label="Régimen Tributario"
                  value={datosFormulario.regimenTributario}
                  onChange={(e) => manejarCambioInput('regimenTributario', e.target.value)}
                  required
                  options={REGIMENES_TRIBUTARIOS}
                  error={erroresFormulario.regimenTributario}
                  disabled={esEmpresaDemo}
                />
                </RevealField>
                </>
                )}
              </div>
            </TarjetaConfiguracion>

            {/* En empresa nueva, los ajustes aparecen solo con RUC validado y sin estado irregular en SUNAT */}
            {mostrarCamposEmpresa && (
            <RevealField show={revealVisible} inmediato={revealInmediato} delayMs={reveal.getDelay(5)}>
            <TarjetaConfiguracion
              title="Ajustes Generales"
              density="compact"
            >
              <div>
                <Select
                  id="empresa-moneda-base"
                  name="empresaMonedaBase"
                  label="Moneda Base"
                  value={datosFormulario.monedaBase}
                  onChange={(e) => manejarCambioInput('monedaBase', e.target.value)}
                  required
                  helperText="Impacta catálogos de productos, emisión y POS"
                  options={monedasToSelectOptions(monedas)}
                  disabled={esEmpresaDemo}
                />
              </div>

            </TarjetaConfiguracion>
            </RevealField>
            )}


            {/* En empresa nueva, el contacto aparece solo con RUC validado y sin estado irregular en SUNAT */}
            {mostrarCamposEmpresa && (
            <RevealField show={revealVisible} inmediato={revealInmediato} delayMs={reveal.getDelay(6)}>
            <TarjetaConfiguracion
              title="Información de Contacto"
              density="compact"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-ui-sm font-medium mb-1.5 text-tertiary">
                    Teléfonos
                  </label>
                  <div className="space-y-3">
                    {datosFormulario.telefonos.map((phone, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="flex-1">
                          <Input
                            id={`empresa-telefono-${index + 1}`}
                            name={`empresaTelefono${index + 1}`}
                            type="tel"
                            value={phone}
                            onChange={(e) => {
                              manejarCambioCampoArray('telefonos', index, e.target.value);
                              if (erroresFormulario[`phone_${index}`]) {
                                setErroresFormulario(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors[`phone_${index}`];
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="+51 987 654 321"
                            leftIcon={<Phone size={18} />}
                            error={erroresFormulario[`phone_${index}`]}
                            disabled={esEmpresaDemo}
                          />
                        </div>
                        {datosFormulario.telefonos.length > 1 && !esEmpresaDemo && (
                          <Button
                            id={`empresa-telefono-eliminar-${index + 1}`}
                            name={`empresaTelefonoEliminar${index + 1}`}
                            type="button"
                            onClick={() => handleClickRemoveTelefono(index)}
                            variant="tertiary"
                            iconOnly
                            size="sm"
                            icon={<X />}
                            className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          />
                        )}
                      </div>
                    ))}
                    {!esEmpresaDemo && (
                      <TextButton
                        id="empresa-telefono-agregar"
                        name="empresaTelefonoAgregar"
                        type="button"
                        intent="primary"
                        size="sm"
                        onClick={handleClickAddTelefono}
                        iconLeft={<Plus size={12} />}
                      >
                        Agregar teléfono
                      </TextButton>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-ui-sm font-medium mb-1.5 text-tertiary">
                    Correos Electrónicos
                  </label>
                  <div className="space-y-3">
                    {datosFormulario.correosElectronicos.map((email, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="flex-1">
                          <Input
                            id={`empresa-correo-${index + 1}`}
                            name={`empresaCorreo${index + 1}`}
                            type="email"
                            value={email}
                            onChange={(e) => {
                              manejarCambioCampoArray('correosElectronicos', index, e.target.value);
                              if (erroresFormulario[`email_${index}`]) {
                                setErroresFormulario(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors[`email_${index}`];
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="contacto@miempresa.com"
                            leftIcon={<Mail size={18} />}
                            error={erroresFormulario[`email_${index}`]}
                            disabled={esEmpresaDemo}
                          />
                        </div>
                        {datosFormulario.correosElectronicos.length > 1 && !esEmpresaDemo && (
                          <Button
                            id={`empresa-correo-eliminar-${index + 1}`}
                            name={`empresaCorreoEliminar${index + 1}`}
                            type="button"
                            onClick={() => handleClickRemoveEmail(index)}
                            variant="tertiary"
                            iconOnly
                            size="sm"
                            icon={<X />}
                            className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          />
                        )}
                      </div>
                    ))}
                    {!esEmpresaDemo && (
                      <TextButton
                        id="empresa-correo-agregar"
                        name="empresaCorreoAgregar"
                        type="button"
                        intent="primary"
                        size="sm"
                        onClick={handleClickAddEmail}
                        iconLeft={<Plus size={12} />}
                      >
                        Agregar correo
                      </TextButton>
                    )}
                  </div>
                </div>
              </div>
            </TarjetaConfiguracion>
            </RevealField>
            )}

            <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                {isFormValid && tieneCambios && !sunatEstadoIrregular && (
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400 animate-in slide-in-from-left duration-300">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-caption font-medium">
                      {isEditMode ? 'Listo para actualizar' : 'Listo para crear empresa'}
                    </span>
                  </div>
                )}
                {isEditMode && !tieneCambios && (
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-caption font-medium">Sin cambios pendientes</span>
                  </div>
                )}
                {sunatEstadoIrregular && (
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <Shield className="w-5 h-5" />
                    <span className="text-caption font-medium">Este RUC no puede registrarse por su estado en SUNAT</span>
                  </div>
                )}
                {!isFormValid && datosFormulario.ruc.length === 11 && !validacionRuc?.isValid && !isEditMode && (
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <Shield className="w-5 h-5" />
                    <span className="text-caption font-medium">Valida el RUC para continuar</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button
                  id="empresa-cancelar"
                  name="empresaCancelar"
                  variant="secondary"
                  onClick={handleClickCancelar}
                >
                  Cancelar
                </Button>

                <Button
                  id="empresa-guardar"
                  name="empresaGuardar"
                  variant="primary"
                  type="submit"
                  disabled={(isEditMode && !tieneCambios) || esGuardando || esCargando || esEmpresaDemo || sunatEstadoIrregular}
                  icon={(esGuardando || esCargando) ? <Loader2 className="animate-spin" /> : undefined}
                >
                  {(esGuardando || esCargando) 
                    ? (isEditMode ? 'Actualizando...' : 'Creando empresa...') 
                    : (isEditMode ? 'Actualizar Empresa' : 'Crear Empresa')}
                </Button>
              </div>
            </div>
          </form>

        </ContenedorForm>
      </div>
    </div>
  );
}
